import {
  Line,
  LineChart,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useEffect, useState } from "react";
import { GoCreditCard, GoPeople } from "react-icons/go";
import arrowup from '../Images/arrowup.svg'
import arrowdown from '../Images/arrowdown.svg'
import Select from "react-select";
import { MdOutlineColorLens } from "react-icons/md";
import { RiExpandUpDownFill } from "react-icons/ri";
import { IoIosArrowForward } from "react-icons/io";

const Dashboard = ({ setDashboardView }) => {
  const [revenueData, setRevenueData] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("asc");
  const [displayedUsers, setDisplayedUsers] = useState([]);

  const getXAxisKey = (trend) => {
    if (!trend || trend.length === 0) return null;

    if ("yearly_created" in trend[0]) return "yearly_created";
    if ("day_created" in trend[0]) return "day_created";
    if ("month_created" in trend[0]) return "month_created";

    return null;
  };

  const formatHoverLabel = (value, key) => {
    const date = new Date(value);

    switch (key) {
      case "yearly_created":
        return date.getFullYear();

      case "month_created":
        return date.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        });

      case "day_created":
        return date.toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
        });

      default:
        return "";
    }
  };

  const TradingLineChart = () => {
    const trend = revenueData?.trend ?? [];

    if (trend.length === 0) {
      return (
        <div className="w-full h-56 flex items-center justify-center bg-[#FCFBFF] rounded-xl">
          <p className="text-sm text-gray-400">No data available</p>
        </div>
      );
    }

    const xKey = getXAxisKey(trend);

    // ✅ Always map revenue safely
    const values = trend.map((d) => d.revenue ?? 0);

    const min = Math.min(...values);
    const max = Math.max(...values);
    const mid = (min + max) / 2;

    return (
      <div className="w-full h-56 bg-[#FCFBFF] rounded-xl p-3">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trend}>
            {/* X AXIS */}
            <XAxis
              dataKey={xKey}
              axisLine={false}
              interval={0}
              padding={{ left: 20, right: 20 }}
              tickLine={false}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              tickFormatter={(value) => formatHoverLabel(value, xKey)}
            />

            {/* Hidden Y axis */}
            <YAxis hide domain={[min, max]} ticks={[min, mid, max]} />

            {/* Horizontal grid lines */}
            <CartesianGrid
              horizontal
              vertical={false}
              stroke="#EDE9FE"
              strokeDasharray="3 3"
            />

            {/* Tooltip */}
            <Tooltip
              labelFormatter={(label) => formatHoverLabel(label, xKey)}
              formatter={(value) => [`₦${value}`, "Revenue"]}
            />

            {/* Line */}
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="#7C3AED"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  useEffect(() => {
    const latest5 = [...users]
      .sort(
        (a, b) =>
          new Date(b?.date_joined) -
          new Date(a?.date_joined)
      )
      .slice(0, 5);
    setDisplayedUsers(latest5);
  }, [users]);

  const yearOpt = [
    { value: "", label: "Lifetime" },
    { value: "2025", label: "2025" },
    { value: "2026", label: "2026" },
    { value: "2027", label: "2027" },
    { value: "2028", label: "2028" },
    { value: "2029", label: "2029" },
    { value: "2030", label: "2030" },
  ];

  const monthOptions = [
    { value: "", label: "All Month" },
    { value: "1", label: "Jan" },
    { value: "2", label: "Feb" },
    { value: "3", label: "Mar" },
    { value: "4", label: "Apr" },
    { value: "5", label: "May" },
    { value: "6", label: "Jun" },
    { value: "7", label: "Jul" },
    { value: "8", label: "Aug" },
    { value: "9", label: "Sep" },
    { value: "10", label: "Oct" },
    { value: "11", label: "Nov" },
    { value: "12", label: "Dec" },
  ];

  const summaryYearOpt = [
    { value: "", label: "Lifetime" },
    { value: "2025", label: "2025" },
    { value: "2026", label: "2026" },
    { value: "2027", label: "2027" },
    { value: "2028", label: "2028" },
    { value: "2029", label: "2029" },
    { value: "2030", label: "2030" },
  ];

  const summaryMonthOptions = [
    { value: "", label: "All Month" },
    { value: "1", label: "Jan" },
    { value: "2", label: "Feb" },
    { value: "3", label: "Mar" },
    { value: "4", label: "Apr" },
    { value: "5", label: "May" },
    { value: "6", label: "Jun" },
    { value: "7", label: "Jul" },
    { value: "8", label: "Aug" },
    { value: "9", label: "Sep" },
    { value: "10", label: "Oct" },
    { value: "11", label: "Nov" },
    { value: "12", label: "Dec" },
  ];

  const [selectedYear, setSelectedYear] = useState(yearOpt[0]);
  const [selectedSummaryYear, setSelectedSummaryYear] = useState(summaryYearOpt[0]);
  const [selectedSummaryMonth, setSelectedSummaryMonth] = useState(summaryMonthOptions[0]);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0]);
  const THEME_COLORS = ["#F04438", "#7F56D9", "#F79009", "#181D27", "#12B76A", "#0BA5EC", "#EE46BC", "#FF692E"];

  const [themes, setThemes] = useState([]);

  const handleYearChange = (option) => {
    setSelectedYear(option);
    setSelectedMonth(monthOptions[0]);
  };

  const handleSummaryYearChange = (option) => {
    setSelectedSummaryYear(option);
  };

  const handleSummaryMonthChange = (option) => {
    setSelectedSummaryMonth(option);
  };

  const handleMonthChange = (option) => {
    setSelectedMonth(option);
  };

  const customStyles = {
    indicatorSeparator: () => ({
      display: 'none',
    }),
    control: (base) => ({
      ...base,
      borderColor: '#F3F0F7',
      paddingTop: '0px',
      paddingBottom: '0px',
      paddingLeft: '7px',
      boxShadow: '0 1px 2px 0 #1D1C1F0D',
      borderRadius: '10px',
      borderWidth: '1px',
      width: 'max-content',
    }),
    menu: (base) => ({
      ...base,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#F3F0F7' : 'white',
      color: 'black',
      padding: 10,
      cursor: 'pointer',
    }),
  };

   useEffect(() => {
    const fetchUsersAndData = async () => {
      try {

        const params = new URLSearchParams();

        if (selectedSummaryYear?.value) {
          params.append("year", selectedSummaryYear.value);
        }

        if (selectedSummaryMonth?.value) {
          params.append("month", selectedSummaryMonth.value);
        }

        const token = localStorage.getItem("PhilomenaAdminToken");
        const res1 = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/admin/get_analytics_overview?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: token ? `Token ${token}` : "",
            },
          }
        );
        const data1 = await res1.json();
        setSummary(data1 || []);
            console.log("=== DASHBOARD ===");



      } catch (err) {
        console.error("Error fetching users:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsersAndData();

  }, [selectedSummaryYear, selectedSummaryMonth]); 

  useEffect(() => {
    const token = localStorage.getItem("PhilomenaAdminToken");
    const fetchThemes = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/themes`, {
          method: "GET",
          headers: { Authorization: `Token ${token}` },
        });
        const data = await res.json();
        setThemes(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchThemes();
  }, []);

  const normalizeThemeName = (name) => {
    return name?.toLowerCase().replace(/\s+/g, "");
  };

  useEffect(() => {
    const token = localStorage.getItem("PhilomenaAdminToken");

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch signup data with selected range as query param

        const params = new URLSearchParams();

        if (selectedYear?.value) {
          params.append("year", selectedYear.value);
        }

        if (selectedMonth?.value) {
          params.append("month", selectedMonth.value);
        }

        const res1 = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/admin/revenue?${params.toString()}`,
          {
            method: "GET",
            headers: { Authorization: `Token ${token}` },
          }
        );
        const data1 = await res1.json();
        setRevenueData(data1);

        const res2 = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/admin/get_user_list`,
          {
            method: "GET",
            headers: { Authorization: `Token ${token}` },
          }
        );
        const data2 = await res2.json();
        setUsers(data2.results);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYear, selectedMonth]);

  const handleSortByName = () => {
    const sorted = [...displayedUsers].sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();

      if (nameA < nameB) return sortOrder === "asc" ? -1 : 1;
      if (nameA > nameB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    setDisplayedUsers(sorted);
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const getPercentage = (label) =>
    summary?.user_breakdown?.find(item => item.label === label)?.percentage || 0;

  const getValue = (label) =>
    summary?.user_breakdown?.find(item => item.label === label)?.count || 0;

  // const getThemePercentage = (theme) =>
  //   summary?.wedding_breakdown?.find(item => item.theme === theme)?.percentage || 0;

  // const getThemeValue = (theme) =>
  //   summary?.wedding_breakdown?.find(item => item.theme === theme)?.wedding_count || 0;

  if (loading) {
    return (
      <div className="text-center py-4 flex flex-col items-center justify-center h-[90vh]">
        <div className="w-8 h-8 border-b-2 border-purple-500 rounded-full animate-spin"></div>
        <span className="text-gray-600">Loading...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-5 space-y-8 sm:py-10">
      <div className="space-y-7">
        <p className="text-2xl font-medium">Overview</p>

        <div className="flex flex-col-reverse items-start justify-between w-full gap-4 sp:flex-row sm:w-auto">
          <div className="space-y-1">
            <p className="text-[#0F132466] font-medium">Total Revenue</p>
            <p className="text-[28px] font-medium"><span className="text-[#0F132466]">₦</span>{revenueData.revenue}</p>
            <div className="flex items-center gap-1">
              <div
                className={`flex items-center gap-2 px-2 py-1 rounded-lg text-sm font-medium
                  ${revenueData?.pct_change < 0
                    ? "bg-red-100 text-red-600"
                    : "bg-[#D1FAE4] text-[#166E3F]"
                  }
                `}
              >
                <img
                  src={revenueData?.pct_change < 0 ? arrowdown : arrowup}
                  alt=""
                />
                <p>{Math.ceil(revenueData?.pct_change)}%</p>
              </div>

              <p className="text-sm text-[#0F132466] font-medium">
                {revenueData?.delta < 0
                  ? `-₦${Math.abs(revenueData.delta)}`
                  : `+₦${revenueData.delta}`}{" "}
                from last 30 days
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Select
              options={yearOpt}
              value={selectedYear}
              onChange={handleYearChange}
              styles={customStyles}
              className="w-full"
              menuPlacement="auto"
              isSearchable={false}
            />
            {selectedYear.value !== '' && (
              <Select
                options={monthOptions}
                value={selectedMonth}
                onChange={handleMonthChange}
                placeholder="Select Month"
                styles={customStyles}
                className="w-full"
                menuPlacement="auto"
                isSearchable={false}
              />
            )}
          </div>
        </div>
      </div>

      <TradingLineChart />

      <div className="flex items-center justify-between w-full gap-4 sm:w-auto">
        <p className="text-[#0F132466] font-medium">Summary</p>

        <div className="flex items-center gap-4">
          <Select
            options={summaryYearOpt}
            value={selectedSummaryYear}
            onChange={handleSummaryYearChange}
            styles={customStyles}
            className="w-full"
            menuPlacement="auto"
            isSearchable={false}
          />
          {selectedSummaryYear.value !== '' && (
            <Select
              options={summaryMonthOptions}
              value={selectedSummaryMonth}
              onChange={handleSummaryMonthChange}
              placeholder="Select Month"
              styles={customStyles}
              className="w-full"
              menuPlacement="auto"
              isSearchable={false}
            />
          )}
        </div>
      </div>
      <div className="grid items-start gap-3 mc:grid-cols-3 sm:grid-cols-2 sm:justify-center sm:gap-5">
        <div className="text-black/50 w-full sm:p-6 p-3 h-full rounded-md flex flex-col justify-between border-[1.5px] border-[#EBECEC] bg-white">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <p className="text-[16px] font-medium">Total Users</p>

              <div className="flex items-center gap-2">
                <p className="sm:text-4xl text-[22px] text-black font-[450]">{summary?.cards?.total_users.value}</p>
                <div
                  className={`flex items-center gap-2 px-2 py-1 rounded-lg text-sm font-medium
                  ${summary?.cards?.total_users?.pct_change < 0
                      ? "bg-red-100 text-red-600"
                      : "bg-[#D1FAE4] text-[#166E3F]"
                    }
                `}
                >
                  <img
                    src={summary?.cards?.total_users?.pct_change < 0 ? arrowdown : arrowup}
                    alt=""
                  />
                  <p>{Math.ceil(summary?.cards?.total_users?.pct_change)}%</p>
                </div>
              </div>
            </div>
            <div className="bg-[#62259A0A] rounded-full text-[#62259A] text-xl size-12 flex items-center justify-center">
              <GoPeople />
            </div>
          </div>
        </div>

        <div className="text-black/50 w-full sm:p-6 p-3 h-full rounded-md flex flex-col justify-between border-[1.5px] border-[#EBECEC] bg-white">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <p className="text-[16px] font-medium">Paid Users</p>

              <div className="flex items-center gap-2">
                <p className="sm:text-4xl text-[22px] text-black font-[450]">{summary?.cards?.paid_users.value}</p>
                <div
                  className={`flex items-center gap-2 px-2 py-1 rounded-lg text-sm font-medium
                  ${summary?.cards?.paid_users?.pct_change < 0
                      ? "bg-red-100 text-red-600"
                      : "bg-[#D1FAE4] text-[#166E3F]"
                    }
                `}
                >
                  <img
                    src={summary?.cards?.paid_users?.pct_change < 0 ? arrowdown : arrowup}
                    alt=""
                  />
                  <p>{Math.ceil(summary?.cards?.paid_users?.pct_change)}%</p>
                </div>
              </div>
            </div>
            <div className="bg-[#62259A0A] rounded-full text-[#62259A] text-xl size-12 flex items-center justify-center">
              <GoCreditCard />
            </div>
          </div>
        </div>

        <div className="text-black/50 w-full sm:p-6 p-3 h-full rounded-md flex flex-col justify-between border-[1.5px] border-[#EBECEC] bg-white">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <p className="text-[16px] font-medium">Weddings Created</p>

              <div className="flex items-center gap-2">
                <p className="sm:text-4xl text-[22px] text-black font-[450]">{summary?.cards?.weddings_created.value}</p>
                <div
                  className={`flex items-center gap-2 px-2 py-1 rounded-lg text-sm font-medium
                  ${summary?.cards?.weddings_created?.pct_change < 0
                      ? "bg-red-100 text-red-600"
                      : "bg-[#D1FAE4] text-[#166E3F]"
                    }
                `}
                >
                  <img
                    src={summary?.cards?.weddings_created?.pct_change < 0 ? arrowdown : arrowup}
                    alt=""
                  />
                  <p>{Math.ceil(summary?.cards?.weddings_created?.pct_change)}%</p>
                </div>
              </div>
            </div>
            <div className="bg-[#62259A0A] rounded-full text-[#62259A] text-xl size-12 flex items-center justify-center">
              <MdOutlineColorLens />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center gap-6 mk:flex-row">
        <div className="text-black/50 w-full space-y-5 sm:p-6 p-3 h-full rounded-md flex flex-col justify-between border-[1.5px] border-[#EBECEC] bg-white">
          <p className="text-[16px] font-medium">User Breakdown</p>
          <p className="sm:text-4xl text-[22px] text-black font-[450]">{summary?.cards?.total_users.value}</p>
          <div className="flex items-center w-full gap-1">
            <div
              className="bg-[#F04438] h-1.5 rounded-full"
              style={{ width: `${getPercentage('Paid')}%` }}
            />

            <div
              className="bg-[#7F56D9] h-1.5 rounded-full"
              style={{ width: `${getPercentage('Unpaid')}%` }}
            />

            <div
              className="bg-[#F79009] h-1.5 rounded-full"
              style={{ width: `${getPercentage('Wedding')}%` }}
            />
          </div>

          <div className="grid gap-4 sa:grid-cols-2">
            <div className="flex items-center gap-2 text-sm">
              <p className="size-2.5 bg-[#F04438]"></p>
              <p>Paid ({Math.ceil(getPercentage('Paid'))}% ∙ {getValue('Paid')})</p>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <p className="size-2.5 bg-[#7F56D9]"></p>
              <p>Unpaid ({Math.ceil(getPercentage('Unpaid'))}% ∙ {getValue('Unpaid')})</p>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <p className="size-2.5 bg-[#F79009]"></p>
              <p>Wedding ({Math.ceil(getPercentage('Wedding'))}% ∙ {getValue('Wedding')})</p>
            </div>
          </div>
        </div>

     

        <div className="text-black/50 w-full space-y-5 sm:p-6 p-3 h-full rounded-md flex flex-col justify-between border-[1.5px] border-[#EBECEC] bg-white">
          <p className="text-[16px] font-medium">Wedding Breakdown</p>
          <p className="sm:text-4xl text-[22px] text-black font-[450]">
            {summary?.cards?.weddings_created.value}
          </p>

          {/* Bars */}
          <div className="flex items-center w-full gap-1">
            {themes.map((theme, i) => {
              const normalized = normalizeThemeName(theme.name);

              const match = summary?.wedding_breakdown?.find(
                (item) => item.theme === normalized
              );

              const pct = match?.percentage || 0;

              return (
                <div
                  key={theme.id}
                  className="h-1.5 rounded-full"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: THEME_COLORS[i % THEME_COLORS.length],
                  }}
                />
              );
            })}
          </div>

          {/* Legend */}
          <div className="grid gap-4 sa:grid-cols-2">
            {themes.map((theme, i) => {
              const normalized = normalizeThemeName(theme.name);

              const match = summary?.wedding_breakdown?.find(
                (item) => item.theme === normalized
              );

              const pct = match ? Math.ceil(match.percentage) : 0;
              const count = match?.wedding_count || 0;

              return (
                <div key={theme.id} className="flex items-center gap-2 text-sm">
                  <p
                    className="size-2.5 shrink-0"
                    style={{
                      backgroundColor: THEME_COLORS[i % THEME_COLORS.length],
                    }}
                  />
                  <p>
                    {theme.name} ({pct}% ∙ {count})
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between w-full gap-4 mt-5 sm:w-auto">
        <p className="text-[#0F132466] font-medium">Recent Users</p>

        <div onClick={() => {
          localStorage.setItem('dashboardView', 'users')
          setDashboardView('users')
          window.scrollTo(0, 0)
        }} className="border border-[#DEE0E3] py-1.5 px-2.5 rounded-xl cursor-pointer">
          View All
        </div>
      </div>

      <div className="mt-6 overflow-x-auto rounded-[10px] border" style={{ WebkitOverflowScrolling: "touch" }}>
        <table className="w-full min-w-[1000px] text-nowrap border-collapse">
          <thead>
            <tr className="bg-[#F7F7F8] text-left text-[#0F132499] text-sm">
              <th onClick={handleSortByName} className="flex items-center gap-1 px-3 py-3 cursor-pointer">
                <span>User Detail</span>
                <RiExpandUpDownFill className="size-4" />
              </th>
              <th className="px-3 py-3">Status</th>
              <th className="px-3 py-3">Wedding Status</th>
              <th className="px-3 py-3">Date Joined</th>
              <th className="px-3 py-3 text-end"></th>
            </tr>
          </thead>
          <tbody>

            {users.length === 0 && (
              <tr>
                <td colSpan="5" className="px-3 py-6 text-center text-gray-600">
                  No users found
                </td>
              </tr>
            )}

            {users.length > 0 &&
              displayedUsers.length > 0 &&
              displayedUsers
                .slice(0, 5)
                .map((user) => (
                  <tr
                    key={user.id}
                    onClick={() => {
                      window.scrollTo(0, 0)
                      setDashboardView('userDetails')
                      localStorage.setItem('dashboardView', 'userDetails')
                      localStorage.setItem('selectedUserId', user?.id)
                    }}
                    className="border-b hover:bg-[#F7F7F8] cursor-pointer text-sm text-gray-800"
                  >
                    <td className="flex items-center gap-2 px-3 py-3">
                      <div className="flex items-center justify-center size-10 rounded-full bg-[#62259A] text-white font-medium">
                        {user?.initials}
                      </div>
                      <div className="flex flex-col">
                        <p className="font-medium">{user?.name}</p>
                        <p className="text-[#0F132499]">{user?.email}</p>
                      </div>
                    </td>

                    <td className="px-3 py-3">
                      {user?.status === "Paid" ? (
                        <span className="inline-flex rounded-lg px-2 py-0.5 text-sm leading-4 text-green-500 bg-green-100 font-medium">
                          Paid
                        </span>
                      ) : (
                        <span className="inline-flex rounded-lg px-2 py-0.5 text-sm leading-4 bg-[#FDEAD8] text-[#AE590A] font-medium">
                          Pending
                        </span>
                      )}
                    </td>

                    <td className="px-3 py-3">
                      {user?.wedding_status === 'Created' ? (
                        <span className="inline-flex rounded-lg px-2 py-0.5 text-sm leading-4 text-[#5314A3] bg-[#ECDFFB] font-medium">
                          Created
                        </span>
                      ) : (
                        <span className="inline-flex rounded-lg px-2 py-0.5 text-sm leading-4 bg-[#E9EAEC] text-[#0F132499] font-medium">
                          Not Created
                        </span>
                      )}
                    </td>

                    <td className="px-3 py-3 font-medium">
                      {new Date(user?.date_joined).toLocaleString("en-US", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>

                    <td className="px-3 py-3 text-end">
                      <IoIosArrowForward />
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default Dashboard;